import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';
import {
  useCreateWorkGroupFolder,
  useWorkGroupContents,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';

const MAX_DEPTH_WARNING =
  'This folder is already at the maximum nesting depth. Create a folder higher up to continue organizing files.';

interface CreateFolderDialogProps {
  workGroupId: string;
  parentFolderId?: string;
  privateAccessReason?: string;
  canCreateChild?: boolean;
}

export function CreateFolderDialog({
  workGroupId,
  parentFolderId,
  privateAccessReason,
  canCreateChild: canCreateChildProp,
}: CreateFolderDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const mutation = useCreateWorkGroupFolder();

  const { data: parentContents } = useWorkGroupContents(
    workGroupId,
    parentFolderId,
    undefined,
    privateAccessReason,
    canCreateChildProp === undefined && !!workGroupId,
  );

  const canCreateChild = canCreateChildProp ?? parentContents?.canCreateChild ?? true;
  const atMaxDepth = !canCreateChild;

  const handleCreate = () => {
    if (atMaxDepth) {
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) {
      toast({ title: 'Folder name is required', variant: 'destructive' });
      return;
    }
    mutation.mutate(
      {
        workGroupId,
        name: trimmed,
        parentId: parentFolderId,
        privateAccessReason,
      },
      {
        onSuccess: () => {
          toast({ title: 'Folder created' });
          setOpen(false);
          setName('');
        },
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={atMaxDepth} title={atMaxDepth ? MAX_DEPTH_WARNING : undefined}>
          New folder
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create folder</DialogTitle>
        </DialogHeader>
        {atMaxDepth ? (
          <p className="text-sm text-destructive" role="alert">
            {MAX_DEPTH_WARNING}
          </p>
        ) : (
          <div className="space-y-2 py-2">
            <Label htmlFor="create-folder-name">Name</Label>
            <Input
              id="create-folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
            />
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            disabled={atMaxDepth || !name.trim() || mutation.isPending}
            onClick={handleCreate}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
